import React, { memo, useState, useCallback } from "react";
import SingleInput from "./SingleInput";

// OTPInputComponent is a functional component for OTP input
export function OTPInputComponent(props) {
  // Destructuring props
  const { length, isNumberInput, autoFocus, disabled, onChangeOTP, inputClassName, inputStyle, handleSubmitOTP, ...rest } = props;

  // State for tracking active input and OTP values
  const [activeInput, setActiveInput] = useState(0);
  const [otpValues, setOTPValues] = useState(Array(length).fill(""));

  // Helper function to handle OTP change
  const handleOtpChange = useCallback(
    (otp) => {
      const otpValue = otp.join("");
      onChangeOTP(otpValue);
    },
    [onChangeOTP]
  );

  // Helper function to handle the right value based on input type
  const getRightValue = useCallback(
    (str) => {
      let changedValue = str;

      if (!isNumberInput || !changedValue) {
        return changedValue;
      }
      return Number(changedValue) >= 0 ? changedValue : "";
    },
    [isNumberInput]
  );

  // Helper function to change OTP value at the focused input
  const changeCodeAtFocus = useCallback(
    (str) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || "";
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues]
  );

  // Helper function to focus a specific input
  const focusInput = useCallback(
    (inputIndex) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length]
  );

  // Helper functions to focus the previous and next inputs
  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  // Event handler for onFocus input
  const handleOnFocus = useCallback(
    (index) => () => {
      focusInput(index);
    },
    [focusInput]
  );

  // Event handler for onChange value for each input
  const handleOnChange = useCallback(
    (e) => {
      const val = getRightValue(e.currentTarget.value);
      if (!val) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput, getRightValue]
  );

  // Event handler for onBlur input
  const onBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  // Event handler for onKeyDown input
  const handleOnKeyDown = useCallback(
    (e) => {
      const pressedKey = e.key;

      switch (pressedKey) {
        case "Backspace":
        case "Delete": {
          e.preventDefault();
          if (otpValues[activeInput]) {
            changeCodeAtFocus("");
          } else {
            focusPrevInput();
          }
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case "ArrowRight": {
          e.preventDefault();
          focusNextInput();
          break;
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault();
          }
          break;
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]
  );

  // Event handler for handling key press to submit OTP
  const handleOnKeyPressSubmit = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmitOTP();
      }
    },
    [handleSubmitOTP]
  );

  // Event handler for handling paste action
  const handleOnPaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text/plain")
        .trim()
        .slice(0, length - activeInput)
        .split("");
      if (pastedData) {
        let nextFocusIndex = 0;
        const updatedOTPValues = [...otpValues];
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = getRightValue(pastedData.shift() || val);
            if (changedValue) {
              updatedOTPValues[index] = changedValue;
              nextFocusIndex = index;
            }
          }
        });
        setOTPValues(updatedOTPValues);
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
        handleOtpChange(updatedOTPValues);
      }
    },
    [activeInput, getRightValue, length, otpValues,handleOtpChange]
  );

  // Rendering the OTP input components
  return Array(length)
    .fill("")
    .map((_, index) => (
      <div {...rest} key={`SingleInput-${index}`}>
        <SingleInput
          type={isNumberInput ? "number" : "text"}
          focus={activeInput === index}
          value={otpValues && otpValues[index]}
          autoFocus={autoFocus}
          onFocus={handleOnFocus(index)}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onBlur={onBlur}
          onPaste={handleOnPaste}
          style={inputStyle}
          className={inputClassName}
          disabled={disabled}
          onKeyPress={handleOnKeyPressSubmit}
        />
      </div>
    ));
}

// Memoized OTPInput component for performance optimization
const OTPInput = memo(OTPInputComponent);
export default OTPInput;
